import React, { useEffect, useState } from "react";
import _ from "lodash/fp";
import { Alert, Dialog } from "@actinc/dls";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useMutation } from "urql";

import { validatePassageName, validateItemName } from "../../../services/utils";
import { Backdrop, ControlledFormInput } from "../../../components";

const RenameDialog = ({
  defaultValues = {
    newName: "",
  },
  id,
  onClose,
  currentName,
  objectType,
  handleRefetch,
}) => {
  const [objectName, setObjectName] = useState({
    oldName: "",
    newName: "",
  });

  const { control, formState, getValues, handleSubmit, reset, watch } = useForm(
    {
      mode: "all",
      reValidateMode: "onBlur",
    }
  );
  const { newName } = getValues();
  const { isDirty, isSubmitting, isValid } = formState;
  const { newName: currentNewNameValue } = watch();

  const handleCancel = () => {
    onClose(null);
  };

  const handleClose = () => {
    reset({ ...defaultValues });
    onClose();
  };

  const onSubmit = () => {
    rename({
      id: id,
      newName: getValues().newName,
    });
    setObjectName({
      oldName: currentName,
      newName: newName,
    });
    reset({ ...defaultValues }, { keepValues: true });
  };

  const [result, rename] = useMutation(
    objectType === "passage" ? renamePassageMutation : renameItemMutation
  );
  const capitalizeFirstChar = _.capitalize(objectType);

  useEffect(() => {
    if (result?.data && result.data[`rename${capitalizeFirstChar}`] !== null) {
      handleRefetch();
    }
  }, [capitalizeFirstChar, result.data, handleRefetch]);

  return (
    <Dialog maxWidth="md" open={true} onClose={handleCancel}>
      <Backdrop loading={result.fetching} />
      <DialogTitle>{`Rename ${capitalizeFirstChar} ${
        objectName?.newName &&
        result?.data &&
        result.data[`rename${capitalizeFirstChar}`] !== null
          ? objectName.newName
          : currentName
      }`}</DialogTitle>
      <form id="renameDialogForm" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ControlledFormInput
            control={control}
            id="newName"
            label="New Name"
            name="newName"
            rules={{
              maxLength: objectType === "passage" ? 50 : 24,
              validate:
                objectType === "passage"
                  ? validatePassageName
                  : validateItemName,
            }}
          />
          {!!result?.data && !isDirty && currentNewNameValue !== "" && (
            <Alert
              severity={
                result?.data &&
                result.data[`rename${capitalizeFirstChar}`] !== null
                  ? "success"
                  : "error"
              }
              sx={{ "& .MuiAlert-icon": { paddingTop: 1.3 } }}
              title={
                result?.data &&
                result.data[`rename${capitalizeFirstChar}`] !== null ? (
                  <>
                    {`${objectName.oldName} has been renamed to ${objectName?.newName}`}
                  </>
                ) : (
                  <>{`The name ${objectName?.newName} already exists in the system`}</>
                )
              }
            />
          )}
        </DialogContent>
      </form>
      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="text">
          Close
        </Button>
        <Button
          aria-label="Rename"
          color="primary"
          disabled={!isValid || isSubmitting || result?.fetching || !isDirty}
          form="renameDialogForm"
          type="submit"
        >
          Rename
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const renameItemMutation = `
  mutation RenameItem($id: ID!, $newName: String!) {
    renameItem(id: $id, newName: $newName) {
      id
      name
    }
  }
`;
const renamePassageMutation = `
  mutation RenamePassage($id: ID!, $newName: String!) {
    renamePassage(id: $id, newName: $newName) {
      id
      name
    }
  }
`;

export { RenameDialog, renameItemMutation };
